import { client } from '@/api/client.gen'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Define our own types that are compatible with both systems
interface AppUser {
  id: string
  email: string
  user_metadata: {
    display_name?: string | null
  }
  created_at?: string | null
  // Optional Supabase fields
  app_metadata?: any
  aud?: string
  confirmation_sent_at?: string
  confirmed_at?: string
  email_confirmed_at?: string
  identities?: any[]
  last_sign_in_at?: string
  phone?: string
  role?: string
  updated_at?: string
}

interface AppSession {
  access_token: string
  refresh_token?: string | null
  user: AppUser
  // Optional Supabase fields
  expires_in?: number
  token_type?: string
  expires_at?: number
  provider_token?: string
  provider_refresh_token?: string
}

interface AuthState {
  user: AppUser | null
  session: AppSession | null
  status: 'initial' | 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
}

const initialState: AuthState = {
  user: null,
  session: null,
  status: 'initial',
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state) => {
      state.status = 'loading'
      state.error = null
    },
    setAuthData: (state, action: PayloadAction<{ user: AppUser; session: AppSession }>) => {
      state.user = action.payload.user
      state.session = action.payload.session
      state.status = 'succeeded'
      state.error = null
      client.setConfig({
        headers: {
          ...client.getConfig().headers,
          Authorization: `Bearer ${action.payload.session.access_token}`,
        },
      })
    },
    clearAuthData: (state) => {
      state.user = null
      state.session = null
      state.status = 'idle'
      state.error = null
      client.setConfig({
        headers: {
          ...client.getConfig().headers,
          Authorization: undefined,
        },
      })
    },
    setError: (state, action: PayloadAction<string>) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
})

export const { setLoading, setAuthData, clearAuthData, setError } = authSlice.actions
export default authSlice.reducer
export type { AppSession, AppUser }
